import { memo, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { IdentifierType } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { VoicemailBox } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/voicemail.pb';
import {
  DeleteVoicemailResponse,
  ViewStatus,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import type { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { UseInfiniteQueryResult, UseMutateFunction } from 'react-query';
import { Row } from 'react-table';
import { NotificationQueries } from '@frontend/api-notifications';
import { TagsUtils } from '@frontend/api-tag';
import { VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Chips } from '@frontend/chips';
import { EmptyStateConfig } from '@frontend/components';
import { useGetDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { AddTag, CachedAudioScrubber } from '@frontend/phone-panels';
import { useQueueStore } from '@frontend/queue';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  CellRendererProps,
  Chip,
  Table,
  TableColumnConfig,
  TableInstance,
  TableStyleConfig,
  TextLink,
  useAlert,
} from '@frontend/design-system';
import { VoicemailFiltersType } from '../../hooks/use-phone-config-state-store';
import {
  useVoicemailTagMutation,
  optimisticAddVoicemailTag,
  optimisticRemoveVoicemailTag,
} from '../../hooks/use-voicemail-tag-mutations';
import { transcriptionTrackingId } from '../../pendo-tracking';
import { useVoicemailDetailsShallowStore } from '../../stores/voicemail-details-store';
import { ProfileCard } from '../all-calls/profile-card';
import { ToolbarChipFilter } from '../all-calls/toolbar-filter';
import { VoicemailInfiniteQueryData, VoicemailQueryData, VoicemailTableDataRow } from '../all-calls/types';
import { DEFAULT_NO_TIME_PERIOD } from '../filter-selectors/time-period-selector';
import { TranscriptionContext } from '../voicemail-details/types';
import { getTranscriptionUniqueKey } from '../voicemail-details/utils';
import { useSyncDetailsPanel } from './panel-sync';
import { VoicemailsTrayFilters } from './vm-filters';
import { VoicemailActionsGetterProps } from './voicemail-actions';
import { VoicemailTagSelector } from './voicemail-tag-selector';
import { hasVMFilterChanged } from './voicemail-utils';
import { SetPageConfigInput } from './voicemails-multi.component';

type VoicemailTableViewFilterConfig = {
  setFilters: (filters: VoicemailFiltersType) => void;
  filters: VoicemailFiltersType;
  voicemailBoxList: VoicemailBox[];
  defaultFilters: VoicemailFiltersType;
  mappedTags: Record<string, Tag>;
  searchHandler: (searchText: string) => void;
};

export type VoicemailColumnId =
  | 'contactName'
  | 'dateTime'
  | 'voicemailMessage'
  | 'contactPhone'
  | 'location'
  | 'voicemailBoxType'
  | 'tags'
  | 'actions';

export const VoicemailTableView = ({
  isLoading,
  getVoicemailActions,
  infiniteQueryProps,
  selectedLocationIds,
  emptyState,
  panelProps,
  setPageConfig,
  pageNumber = 1,
  pageSize = 25,
  filterConfig,
  columnIds,
  showNumberRowSelector = true,
  styleConfig,
  fullHeightConfig,
  showVmTranscriptionButton = true,
}: {
  isLoading?: boolean;
  infiniteQueryProps: UseInfiniteQueryResult<VoicemailInfiniteQueryData, unknown>;
  getVoicemailActions: (props: VoicemailActionsGetterProps) => (React.ReactElement | null)[];
  selectedLocationIds: string[];
  emptyState?: EmptyStateConfig;
  panelProps: {
    updateReadStatus: UseMutateFunction<
      VmTypes.SetVoicemailViewStatusResponse,
      unknown,
      VmTypes.StatusPayload,
      unknown
    >;
    deleteVoicemail: UseMutateFunction<DeleteVoicemailResponse, unknown, VmTypes.DeletePayload, void>;
    voicemailsQueryKey: string[];
    sectionFocus: TranscriptionContext['sectionFocus'];
  };
  setPageConfig: (config: SetPageConfigInput) => void;
  pageNumber?: number;
  pageSize?: number;
  filterConfig?: VoicemailTableViewFilterConfig;
  columnIds?: VoicemailColumnId[];
  fullHeightConfig?: {
    offset?: number;
    maxHeight?: number;
    minHeight?: number;
  };
  showNumberRowSelector?: boolean;
  styleConfig?: TableStyleConfig<VoicemailTableDataRow, VoicemailColumnId>;
  showVmTranscriptionButton?: boolean;
}) => {
  const { t } = useTranslation('calls');
  const markAsReadMutation = NotificationQueries.useMarkNotificationsAsRead();
  const { getScopeName } = useAppScopeStore();
  const showEnabledFiltersBadgeHandler = useRef<((showNotification: boolean) => void) | undefined>(undefined);

  const setFilters = useCallback(
    (filters: VoicemailFiltersType) => {
      if (filterConfig) {
        filterConfig.setFilters(filters);
        showEnabledFiltersBadgeHandler.current?.(hasVMFilterChanged(filters, filterConfig.defaultFilters));
      }
    },
    [filterConfig]
  );

  const getLocationChip = useCallback(
    (locationIds: string[]) => {
      if (locationIds.length > 1) {
        return (
          <Chips.LocationChip>
            {locationIds.length} {t('Locations')}
          </Chips.LocationChip>
        );
      } else if (locationIds.length === 1) {
        return <Chips.LocationChip>{getScopeName(locationIds[0])}</Chips.LocationChip>;
      }
      return <span>{'--'}</span>;
    },
    [t, getScopeName]
  );

  const [tableInstance, setTableInstance] = useState<TableInstance<VoicemailTableDataRow>>();

  const toggleRowSelected = useCallback(
    (id: string, set: boolean, index?: number) => {
      tableInstance?.toggleAllRowsSelected(false);
      tableInstance?.toggleRowSelected(id, set);
      if (set && index !== undefined) {
        const rowElement = document.querySelectorAll(`#${tableInstance?.tableId} [role=row]`)[index];
        rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    [tableInstance]
  );

  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const isVMTranscriptionEnabled = getCustomizationFlagValue('voicemailtranscription') && showVmTranscriptionButton;

  const { data: voicemailsData, fetchNextPage, refetch, hasNextPage } = infiniteQueryProps;

  const tableData = useMemo(() => voicemailsData?.pages[pageNumber - 1]?.rows || [], [voicemailsData, pageNumber]);

  const {
    instanceId,
    currentRowId,
    setPageSize,
    setUpdateReadStatus,
    setDeleteVoicemail,
    setInstanceId,
    setRows,
    setToggleRowSelected,
    setVoicemailsQueryKey,
    setSectionFocus,
    setUserHasAccess,
    setIsDetached,
  } = useVoicemailDetailsShallowStore(
    'setInstanceId',
    'currentRowId',
    'setPageSize',
    'setUpdateReadStatus',
    'setDeleteVoicemail',
    'setInstanceId',
    'setRows',
    'setToggleRowSelected',
    'setVoicemailsQueryKey',
    'setSectionFocus',
    'setUserHasAccess',
    'setIsDetached'
  );

  useSyncDetailsPanel({
    shouldSync: tableInstance !== undefined && instanceId !== tableInstance.tableId,
    instanceId: tableInstance?.tableId ?? '',
    toggleRowSelected,
    markCurrentRowSelected: () => {
      if (currentRowId && tableInstance) {
        const row = tableInstance.rowsById[currentRowId];
        if (row) {
          toggleRowSelected(currentRowId, true, row.index);
        }
      }
    },
  });

  const setupDetailsPanel = useCallback(() => {
    if (tableInstance) {
      setUpdateReadStatus(panelProps.updateReadStatus);
      setDeleteVoicemail(panelProps.deleteVoicemail);

      setPageSize(pageSize);
      setUpdateReadStatus(panelProps.updateReadStatus);
      setDeleteVoicemail(panelProps.deleteVoicemail);
      setInstanceId(tableInstance.tableId);

      setRows(tableInstance.rows.map((row) => row.original));
      setToggleRowSelected(toggleRowSelected);
      setVoicemailsQueryKey(panelProps.voicemailsQueryKey);
      setSectionFocus(panelProps.sectionFocus);
      setUserHasAccess(true);
      setIsDetached(false);
    }
  }, [
    tableInstance,
    setUpdateReadStatus,
    panelProps.updateReadStatus,
    panelProps.deleteVoicemail,
    panelProps.voicemailsQueryKey,
    panelProps.sectionFocus,
    setDeleteVoicemail,
    setPageSize,
    pageSize,
    setInstanceId,
    setRows,
    setToggleRowSelected,
    toggleRowSelected,
    setVoicemailsQueryKey,
    setSectionFocus,
    setUserHasAccess,
    setIsDetached,
  ]);

  const showVoicemailDetailsPanel = useCallback(
    ({ row }: { row: Row<VoicemailTableDataRow> }) => {
      setupDetailsPanel();
      invokeVoicemailDetailsPanel({ row });
    },
    [setupDetailsPanel]
  );

  const markNotificationAsRead = useCallback(
    (voicemailId: string) =>
      markAsReadMutation.mutate({
        notificationType: NotificationType.VOICEMAIL,
        identifiers: [
          {
            key: 'voicemailId',
            val: voicemailId,
            type: IdentifierType.STRING,
          },
        ],
      }),
    []
  );

  const allColumns: Record<VoicemailColumnId, TableColumnConfig<VoicemailTableDataRow>> = useMemo(
    () => ({
      contactName: {
        Header: t('Contact Name'),
        accessor: (row: VoicemailTableDataRow) => row,
        cellRenderer: (row: VoicemailTableDataRow) => <VoicemailContactCell row={row} />,
        disableSortBy: true,
        id: 'contactName' as const,
        mobileLayoutConfig: {
          order: -1,
          spanFullWidth: false,
          hideHeader: true,
          leftShift: 48,
        },
        width: 190,
      },
      dateTime: {
        Header: t('Time'),
        accessor: 'dateTime' as const,
        disableSortBy: true,
        id: 'dateTime' as const,
        mobileLayoutConfig: {
          spanFullWidth: true,
        },
        width: 200,
      },
      voicemailMessage: {
        Header: t('Voicemail'),
        cellRenderer: (_: any, rowData: VoicemailTableDataRow | undefined, row: any) =>
          rowData ? (
            <>
              {rowData.mediaFilePath ? (
                <CachedAudioScrubber
                  trackingId='nwx-phones-voicemails-tableRow-audioScrubber'
                  onPlay={() => {
                    // There should always be a row
                    if (rowData && row?.index !== undefined) {
                      if (!row.readAt) {
                        markNotificationAsRead(rowData?.forwardedMessageId || rowData.channelId);
                      }
                      panelProps.updateReadStatus({
                        index: row.index,
                        voicemailId: rowData?.forwardedMessageId || rowData.channelId,
                        status: ViewStatus.VIEW_STATUS_READ,
                      });
                    }
                  }}
                  filePath={rowData.mediaFilePath}
                  mediaId={`voicemail-${rowData.mediaId}`}
                  allowFetchError
                />
              ) : (
                <span css={styles.noRecordingText}>{'--'}</span>
              )}
            </>
          ) : null,
        disableSortBy: true,
        id: 'voicemailMessage' as const,
        mobileLayoutConfig: {
          spanFullWidth: true,
        },
        minWidth: 300,
      },
      contactPhone: {
        Header: t('Contact Number'),
        accessor: 'contactNumber' as const,
        disableSortBy: true,
        id: 'contactPhone' as const,
        width: 150,
      },
      location: {
        Header: t('Location'),
        accessor: 'locationIds' as const,
        cellRenderer: (locationIds: string[]) => getLocationChip(locationIds ?? []),
        id: 'location' as const,
        omit: !(selectedLocationIds.length > 1),
        disableSortBy: true,
      },
      voicemailBoxType: {
        Header: t('Voicemail Box'),
        accessor: 'voiceMailBoxName' as const,
        disableSortBy: true,
        id: 'voicemailBoxType' as const,
      },
      tags: {
        Header: t('Tags'),
        accessor: (item: VoicemailTableDataRow) => item,
        id: 'tags' as const,
        disableSortBy: true,
        cellRenderer: (record: VoicemailTableDataRow, _rowData: any, row: any) => {
          return (
            <TagsCell
              {...record}
              showVoicemailDetailsPanel={showVoicemailDetailsPanel}
              pageSize={pageSize}
              row={row}
              voicemailsQueryKey={panelProps.voicemailsQueryKey}
            />
          );
        },
      },
      actions: {
        Header: '',
        cellRenderer: (_: any, rowData: VoicemailTableDataRow | undefined, row: any) => {
          if (!rowData) {
            return null;
          }

          return (
            <ActionCell
              rowProps={{
                rowData,
                rowObject: row,
              }}
              isVMTranscriptionEnabled={isVMTranscriptionEnabled}
              getVoicemailActions={getVoicemailActions}
              showVoicemailDetailsPanel={showVoicemailDetailsPanel}
            />
          );
        },
        disableSortBy: true,
        disableColumnFilter: true,
        id: 'actions' as const,
        sticky: 'right' as const,
        width: isVMTranscriptionEnabled ? 100 : 80,
        mobileLayoutConfig: {
          order: -1,
          hideHeader: true,
          spanFullWidth: false,
        },
      },
    }),
    [
      t,
      selectedLocationIds,
      isVMTranscriptionEnabled,
      panelProps,
      getLocationChip,
      showVoicemailDetailsPanel,
      pageSize,
      getVoicemailActions,
    ]
  );

  const colConfig = useMemo(() => {
    const defaultColumnOrder: VoicemailColumnId[] = [
      'contactName',
      'dateTime',
      'voicemailMessage',
      'contactPhone',
      'location',
      'voicemailBoxType',
      'tags',
      'actions',
    ];

    const columnsToShow = columnIds ?? defaultColumnOrder;

    return columnsToShow.filter((columnId) => columnId in allColumns).map((columnId) => allColumns[columnId]);
  }, [columnIds, allColumns]);

  const { enqueue } = useQueueStore.getState();

  return (
    <TableObserver
      onTableRender={(tbInstance) => {
        setTableInstance(tbInstance);
      }}
      render={({ ref }) => (
        <Table
          allowMobileView={false}
          uniqueRowId={(row, index) => {
            return getTranscriptionUniqueKey(row) ?? index;
          }}
          tableInstanceId='multi-voicemails'
          instanceRef={ref}
          globalTrackingId='phone-portal-weave2.0-voicemails'
          data={tableData}
          keepPreviousDataWhileLoading={tableData.length > 0}
          isLoading={isLoading}
          fullHeight
          isPaginated
          hasResponsiveColWidths
          hasResizeColumns
          {...(filterConfig && { hasFilterColumns: true })}
          {...(filterConfig?.searchHandler
            ? {
                hasGlobalSearch: true,
                globalSearchConfig: {
                  initialValue:
                    filterConfig.filters.filterOptions.callerNumber ||
                    filterConfig.filters.filterOptions.callerName ||
                    '',
                  searchHandler: filterConfig.searchHandler,
                  placeholder: t('Search name or 10-digit number'),
                  debounceDelay: 1000,
                },
              }
            : {
                hasGlobalSearch: false,
              })}
          {...(filterConfig && {
            manualFilters: true,
            manualFiltersOptions: {
              initialBadgeState: hasVMFilterChanged(filterConfig.filters, filterConfig.defaultFilters),
            },
            manualFiltersRender: (modalProps, setShowNotificationBadge) => {
              showEnabledFiltersBadgeHandler.current = setShowNotificationBadge;

              return (
                <VoicemailsTrayFilters
                  key={JSON.stringify(filterConfig.filters)}
                  defaultFilters={filterConfig.defaultFilters}
                  filters={filterConfig.filters}
                  resetFilters={() => setFilters(filterConfig.defaultFilters)}
                  mailboxes={filterConfig.voicemailBoxList}
                  onChange={setFilters}
                  isMulti={selectedLocationIds.length > 1}
                  modalProps={modalProps}
                  mappedTags={filterConfig.mappedTags}
                />
              );
            },
          })}
          emptyStateConfig={
            emptyState ?? {
              type: 'voicemail',
              header: t('No Items to Display'),
              description: t('Voicemails will display here.'),
            }
          }
          {...(filterConfig && {
            customToolbarRender: () => (
              <ToolbarChipFilter
                defaultTimePeriod={DEFAULT_NO_TIME_PERIOD}
                trackingBaseId='phone-weave2.0-voicemails'
                filters={{
                  locationIds: filterConfig.filters.locationIds,
                  startDate: filterConfig.filters.filterOptions.startTime ?? '',
                  endDate: filterConfig.filters.filterOptions.endTime ?? '',
                }}
                onChange={(changedfilter) => {
                  setFilters({
                    ...filterConfig.filters,
                    locationIds: changedfilter.locationIds,
                    filterOptions: {
                      ...filterConfig.filters.filterOptions,
                      startTime: changedfilter.startDate,
                      endTime: changedfilter.endDate,
                    },
                  });
                }}
              />
            ),
          })}
          styleConfig={styleConfig}
          fullHeightConfig={fullHeightConfig}
          colConfig={colConfig}
          /**
           * (!)
           * Hide the transcription panel after pagination settings have changed to prevent the panel from using stale data.
           * More info in VoicemailDetailsPanel.
           */
          manualPaginationConfig={{
            handleChange: (action: 'next' | 'prev') => {
              if (action === 'next') {
                /**
                 * We potentially refetch data if the user navigates to the a page where data is already cached.
                 *
                 * The cached page could have less data than expected with optimistic updates on deletion,
                 * so we refresh only if it has a next page and doesn't have a full page of data.
                 */
                if (voicemailsData?.pages[pageNumber]) {
                  refetch<VoicemailQueryData>({
                    refetchPage: (lastPage) => {
                      return lastPage.meta.hasNext && lastPage.rows.length < pageSize;
                    },
                  });
                } else {
                  fetchNextPage();
                }
                setPageConfig((prev) => ({ pageNumber: prev.pageNumber + 1 }));
                /** (!) */
                enqueue('voicemail-details', {
                  payload: {
                    type: 'close',
                  },
                });
              } else {
                setPageConfig((prev) => ({ pageNumber: prev.pageNumber - 1 }));
                /** (!) */
                enqueue('voicemail-details', {
                  payload: {
                    type: 'close',
                  },
                });
              }
            },
            hasNext: pageNumber < (voicemailsData?.pages.length ?? 0) || hasNextPage,
            hasPrevious: true,
            page: pageNumber,
            defaultRowsPerPage: pageSize,
            showNumRowSelector: showNumberRowSelector,
            onNumRowsChange: (num) => {
              setPageConfig({ pageNumber: 1, pageSize: num });
              /** (!) */
              enqueue('voicemail-details', {
                payload: {
                  type: 'close',
                },
              });
            },
            rowsPerPageOptions: [10, 25, 50, 75],
          }}
          wrapperStyle={css`
            .table-toolbar {
              padding-top: ${theme.spacing(1)};
            }
          `}
        />
      )}
    />
  );
};

/**
 * This component is used solely to observe the table instance and pass the instance to the parent component.
 */
const TableObserver = ({
  onTableRender,
  render,
}: {
  onTableRender?: (tableInstance: TableInstance<VoicemailTableDataRow>) => void;
  render: ({ ref }: { ref: MutableRefObject<TableInstance<VoicemailTableDataRow>> }) => ReactNode;
}) => {
  const tableInstanceRef = useRef<TableInstance<VoicemailTableDataRow>>(
    null as unknown as TableInstance<VoicemailTableDataRow>
  );

  // This effect intentionally has no dependencies to ensure it runs after the component has rendered.
  useEffect(() => {
    onTableRender?.(tableInstanceRef.current);
  });

  return render({ ref: tableInstanceRef });
};

type TagsCellProps = VoicemailTableDataRow & {
  voicemailsQueryKey: string[];
  row: Row<VoicemailTableDataRow>;
  pageSize: number;
  showVoicemailDetailsPanel: ({ row }: { row: Row<VoicemailTableDataRow> }) => void;
};

const TagsCell = ({ tags, voicemailId, row, voicemailsQueryKey, showVoicemailDetailsPanel }: TagsCellProps) => {
  let isEditing = false;
  if (row?.state && 'isEditing' in row.state && typeof row.state.isEditing === 'boolean') {
    isEditing = row.state.isEditing;
  }

  if (!row) return null;

  return (
    <TagColumn
      tags={tags}
      voicemailId={voicemailId}
      showPanel={() => showVoicemailDetailsPanel({ row })}
      queryKey={voicemailsQueryKey}
      isEditing={isEditing}
      setIsEditing={(val) => row.setState({ isEditing: val })}
    />
  );
};

export const TagColumn = ({
  tags,
  voicemailId,
  queryKey,
  showPanel,
  isEditing,
  setIsEditing,
}: {
  tags: Tag[];
  voicemailId: string;
  queryKey: string[];
  showPanel: () => void;
  isEditing?: boolean;
  setIsEditing: (val: boolean) => void;
}) => {
  const alerts = useAlert();
  const { t } = useTranslation('calls');

  const { addTag } = useVoicemailTagMutation({
    queryKey: queryKey,
    voicemailId: voicemailId,
    optimisticAddTag: (args) => optimisticAddVoicemailTag(args) || args.oldData,
    optimisticRemoveTag: (args) => optimisticRemoveVoicemailTag(args) || args.oldData,
    onSuccess: ({ op }) => {
      if (op === 'add') alerts.success(t('Tag added successfully.'));
    },
    onFailure: ({ op }) => {
      if (op === 'add') alerts.error(t('Failed to add tag.'));
    },
  });

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0px, 1fr) auto auto',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      {tags.length > 0 && <TagList tags={tags} voicemailId={voicemailId} queryKey={queryKey} />}
      {!isEditing && tags.length > 1 && (
        <TextLink
          css={{
            ':hover': {
              textDecoration: 'none',
            },
          }}
          onClick={() => {
            showPanel();
          }}
        >
          +{tags.length - 1}
        </TextLink>
      )}
      {!isEditing && tags.length === 0 && <AddTag existingTagIds={tags.map((tag) => tag.id)} onApplyTag={addTag} />}

      {isEditing && (
        <VoicemailTagSelector
          existingTagIds={tags.map((tag) => tag.id)}
          onApplyTag={addTag}
          onClose={() => {
            setIsEditing(false);
          }}
          onSelect={() => {
            setIsEditing(false);
          }}
        />
      )}
    </div>
  );
};

const TagList = ({ tags, voicemailId, queryKey }: { tags: Tag[]; voicemailId: string; queryKey: string[] }) => {
  const { t } = useTranslation('calls');
  const alerts = useAlert();

  const { removeTag } = useVoicemailTagMutation({
    queryKey,
    voicemailId,
    optimisticRemoveTag: (args) => optimisticRemoveVoicemailTag(args) || args.oldData,
    optimisticAddTag: (args) => optimisticAddVoicemailTag(args) || args.oldData,
    onSuccess: ({ op }) => {
      if (op === 'remove') alerts.success(t('Successfully removed tag.'));
    },
    onFailure: ({ op }) => {
      if (op === 'remove') alerts.error(t('Failed to remove tag.'));
    },
  });
  const representativeTag = tags[0];

  return (
    <>
      <Chip.Tag
        isRemovable
        onRemoveClick={() => {
          removeTag(representativeTag);
        }}
        css={{ width: 'auto' }}
        color={TagsUtils.convertStringToTagColor(representativeTag.color)}
      >
        {representativeTag.name}
      </Chip.Tag>
    </>
  );
};

const ActionCell = memo(
  ({
    rowProps: { rowData, rowObject: row },
    isVMTranscriptionEnabled = false,
    getVoicemailActions,
    showVoicemailDetailsPanel,
  }: {
    rowProps: Pick<CellRendererProps<VoicemailTableDataRow>, 'rowData' | 'rowObject'>;
    isVMTranscriptionEnabled?: boolean;
    getVoicemailActions: (props: VoicemailActionsGetterProps) => (React.ReactElement | null)[];
    showVoicemailDetailsPanel: ({ row }: { row: Row<VoicemailTableDataRow> }) => void;
  }) => {
    if (!rowData) {
      return null;
    }

    const addTag = () => {
      if (row) {
        row.setState({ isEditing: true });
      }
    };

    const actions = getVoicemailActions({
      index: row?.index ?? 0,
      rowData,
      addTag,
      isInsidePopoverDropdown: true,
    });

    return (
      <>
        <div style={{ width: '100%', display: 'flex', gap: theme.spacing(1), justifyContent: 'center' }}>
          <AdaptoActions
            iconColor='inherit'
            maxActions={isVMTranscriptionEnabled ? 2 : 1}
            css={css`
              & > button {
                width: 32px;
              }
            `}
          >
            {isVMTranscriptionEnabled && row && (
              <TranscriptButton row={row} showVoicemailDetailsPanel={showVoicemailDetailsPanel} />
            )}
            {actions}
          </AdaptoActions>
        </div>
      </>
    );
  }
);

ActionCell.displayName = 'ActionCell';

const invokeVoicemailDetailsPanel = ({ row }: { row: Row<VoicemailTableDataRow> }) => {
  if (!row) {
    return;
  }
  const { enqueue } = useQueueStore.getState();
  enqueue('voicemail-details', {
    payload: {
      type: 'open-with-source',
      data: row.original,
      id: row.id,
      index: row.index,
    },
  });
};

const TranscriptButton = ({
  row,
  hoverLabel,
  showVoicemailDetailsPanel,
}: {
  row: Row<VoicemailTableDataRow>;
  hoverLabel?: string;
  showVoicemailDetailsPanel: ({ row }: { row: Row<VoicemailTableDataRow> }) => void;
}) => {
  const { t } = useTranslation('calls');

  return (
    <AdaptoActions.Action
      icon='document-filled'
      onClick={() => showVoicemailDetailsPanel({ row })}
      trackingId={transcriptionTrackingId({ subComponent: 'table', context: 'action-menu' })}
      aria-label={t('Transcription')}
      hoverLabel={hoverLabel || t('Transcription')}
      color={row.isSelected ? 'primary' : 'light'}
    />
  );
};

const styles = {
  noRecordingText: css`
    color: ${theme.colors.neutral90};
    display: inline-block;
    text-align: left;
    width: 100%;
  `,
};

const VoicemailContactCell = ({ row }: { row: VoicemailTableDataRow }) => {
  const { firstName, lastName, legalName } = useGetDisplayName({ person: row });
  const { t } = useTranslation('calls');

  if (!row) {
    return null;
  }
  return (
    <ProfileCard
      trackingId={`phone-portal-weave2.0-voicemails-profilebtn-tablerowaction-viewprofile`}
      firstName={firstName}
      lastName={lastName}
      potentialName={row.callerName && !isNaN(+row?.callerName) ? '' : row?.callerName}
      shouldUsePotentialName={true}
      defaultName={t('Unknown')}
      locationId={row.locationIds[0]}
      personID={row.contactId}
      markedUnread={!row.readAt}
      title={legalName}
    />
  );
};
